import React, { useState, useEffect } from 'react';
import './SchoolsTable.css';
import OrdersTable from './OrdersTable';
import DistrictOps from './DistrictOps';
import ImportBagsButton from './ImportBagsButton';

function DistrictDetail({ district, loading, onBack, onOpenSchool, onRefresh, onRefreshDistricts }) {
  // Overview = the schools/users/orders roll-up. Manage = acting as the district admin
  // (distribution, moves, requests, rollover) — see DistrictOps.
  const [view, setView] = useState('overview');
  // Read-only test badge in the header; the Mark-test toggle lives in the districts list.
  const [internalOn, setInternalOn] = useState(!!district?.internal);
  useEffect(() => { setInternalOn(!!district?.internal); }, [district?.internal]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteRole, setInviteRole] = useState('district'); // 'district' | 'administrator'
  const [inviteEmails, setInviteEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);
  const [inviteErr, setInviteErr] = useState(null);

  const openInvite = () => { setShowInvite(true); setInviteRole('district'); setInviteEmails(''); setInviteMsg(null); setInviteErr(null); };
  const closeInvite = () => { if (sending) return; setShowInvite(false); };

  // Delete a district user (type-to-confirm).
  const [delUser, setDelUser] = useState(null);
  const [delText, setDelText] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState(null);
  const openDel = (u) => { setDelUser(u); setDelText(''); setDelErr(null); };
  const closeDel = () => { if (delBusy) return; setDelUser(null); setDelText(''); setDelErr(null); };
  const handleDelUser = async () => {
    if (delBusy || !delUser) return;
    setDelBusy(true); setDelErr(null);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/users/${delUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Basic ' + btoa(`${username}:${password}`) }
      });
      if (response.ok) {
        setDelUser(null);
        if (onRefresh) await onRefresh();
      } else {
        setDelErr('Failed to delete account. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting district user:', err);
      setDelErr('Error deleting account. Please try again.');
    } finally {
      setDelBusy(false);
    }
  };

  const sendInvites = async () => {
    if (sending) return;
    const emails = inviteEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@'));
    if (emails.length === 0) { setInviteErr('Enter at least one valid email.'); return; }
    setSending(true); setInviteErr(null); setInviteMsg(null);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/districts/${district.id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic ' + btoa(`${username}:${password}`) },
        body: JSON.stringify({ emails, role: inviteRole })
      });
      if (response.ok) {
        setInviteMsg(`Sent ${emails.length} invite${emails.length === 1 ? '' : 's'}.`);
        setInviteEmails('');
      } else {
        const data = await response.json().catch(() => ({}));
        setInviteErr(data.error || 'Failed to send invites. Please try again.');
      }
    } catch (err) {
      console.error('Error sending district invites:', err);
      setInviteErr('Error sending invites. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="school-detail">
      <div className="school-detail-header">
        <div className="header-left">
          <button onClick={onBack} className="back-button">
            ← Back to Districts
          </button>
          <div className="school-info">
            <h2>{district?.name || 'District'}{internalOn && <span className="test-badge">INTERNAL</span>}</h2>
            {district && (
              <p className="school-district">
                {district.schools?.length || 0} school{(district.schools?.length || 0) === 1 ? '' : 's'} · {district.totalBags} bag{district.totalBags === 1 ? '' : 's'} in system
              </p>
            )}
          </div>
          {district && (
            <>
              <button className="invite-district-button" onClick={openInvite}>
                + Invite district user
              </button>
              {/* Units land in the district's pool, ready to allocate out under Manage as district. */}
              <ImportBagsButton
                districtId={district.id}
                targetName={district.name}
                className="import-bags-button district-header"
                label="Import bags CSV"
                onImported={onRefresh}
              />
            </>
          )}
        </div>
      </div>

      {loading || !district ? (
        <div className="no-data">Loading district…</div>
      ) : (
        <div className="district-detail-body">
          <div className="district-view-tabs">
            <button
              className={`district-view-tab ${view === 'overview' ? 'active' : ''}`}
              onClick={() => setView('overview')}
            >
              Overview
            </button>
            <button
              className={`district-view-tab ${view === 'manage' ? 'active' : ''}`}
              onClick={() => setView('manage')}
            >
              Manage as district
            </button>
          </div>

          {view === 'manage' ? (
            <DistrictOps districtId={district.id} districtName={district.name} />
          ) : (
          <>
          <div className="content-header">
            <h2>Schools ({district.schools.length})</h2>
          </div>
          {district.schools.length === 0 ? (
            <div className="no-data">No schools in this district yet. A school is created when someone joins with the district's "New school" code.</div>
          ) : (
            <table className="schools-table">
              <thead>
                <tr><th>ID</th><th>Name</th></tr>
              </thead>
              <tbody>
                {district.schools.map((s) => (
                  <tr
                    key={s.id}
                    className="clickable-row"
                    onClick={() => onOpenSchool && onOpenSchool(s.id)}
                  >
                    <td>{s.id}</td>
                    <td>{s.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="content-header" style={{ marginTop: 24 }}>
            <h2>District users ({district.districtUsers ? district.districtUsers.length : 0})</h2>
          </div>
          {!district.districtUsers || district.districtUsers.length === 0 ? (
            <div className="no-data">No district-level users yet. Convert a school admin to a district user (Edit role on their account), or invite one above.</div>
          ) : (
            <table className="schools-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th></th></tr>
              </thead>
              <tbody>
                {district.districtUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.role}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="delete-account-button" onClick={() => openDel(u)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 24 }}>
            <OrdersTable
              orders={district.orders}
              onRefreshOrders={onRefresh}
              emptyText="No claimed orders in this district yet."
            />
          </div>
          </>
          )}
        </div>
      )}

      {delUser && (
        <div className="modal-overlay" onClick={closeDel}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete district user</h3>
              <button className="modal-close" onClick={closeDel}>×</button>
            </div>
            <div className="modal-body">
              <p>This permanently deletes the account for:</p>
              <p className="invite-details"><strong>{delUser.name}</strong><br />{delUser.email}</p>
              <p className="warning-text">This cannot be undone. To confirm, type the account's email below.</p>
              <div className="form-group">
                <label>Type <strong>{delUser.email}</strong> to confirm</label>
                <input type="text" value={delText} onChange={(e) => setDelText(e.target.value)} placeholder={delUser.email} autoFocus />
              </div>
              {delErr && <p className="warning-text">{delErr}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={closeDel} disabled={delBusy}>Cancel</button>
              <button
                className="modal-button delete-confirm"
                onClick={handleDelUser}
                disabled={delBusy || delText.trim().toLowerCase() !== (delUser.email || '').trim().toLowerCase()}
              >
                {delBusy ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <div className="modal-overlay" onClick={closeInvite}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite to {district?.name}</h3>
              <button className="modal-close" onClick={closeInvite}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="dist-invite-role">Role</label>
                <select
                  id="dist-invite-role"
                  className="role-select"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  <option value="district">District user (manages the district)</option>
                  <option value="administrator">School admin (creates a new school in this district)</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="dist-invite-emails">Email addresses</label>
                <textarea
                  id="dist-invite-emails"
                  className="email-textarea"
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  placeholder="Separate multiple emails with commas or new lines"
                  rows="4"
                />
                <small className="form-hint">Each person gets an email with a join link.</small>
              </div>
              {inviteMsg && <p className="warning-text" style={{ color: '#2e7d32' }}>{inviteMsg}</p>}
              {inviteErr && <p className="warning-text">{inviteErr}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={closeInvite} disabled={sending}>
                Close
              </button>
              <button className="modal-button submit" onClick={sendInvites} disabled={sending || !inviteEmails.trim()}>
                {sending ? 'Sending…' : 'Send invites'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DistrictDetail;
